import React, { useState, useRef, useEffect } from 'react';
import imageCompression from 'browser-image-compression';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import SEO from '@/components/common/SEO';
import { listingsAPI } from '@/lib/api/listings';
import { apiClient } from '@/lib/api-client';
import { locationData } from '@/lib/indianLocations';
import {
  Camera,
  X,
  Tag,
  MapPin,
  IndianRupee,
  FileText,
  ChevronRight,
  ChevronLeft,
  CheckCircle2,
  Loader2,
  AlertCircle,
  Sparkles,
  ImagePlus,
  Star,
} from 'lucide-react';

// Backend enum values (must match Listing.Category exactly)
const CATEGORIES = [
  { value: 'CARS', label: 'Cars' },
  { value: 'BIKES', label: 'Bikes' },
  { value: 'MOBILES', label: 'Mobiles' },
  { value: 'REAL_ESTATE', label: 'Real Estate' },
  { value: 'RENT_ROOMMATES', label: 'House Rents / Room Mates' },
  { value: 'BUS_TRUCKS_AUTOS_VEHICLES', label: 'Bus / Trucks / Autos / Other Vehicles' },
  { value: 'AUTOMOBILE_ACCESSORIES', label: 'Automobile Accessories' },
  { value: 'PETS_AND_HOBBIES', label: 'Pets & Hobbies' },
  { value: 'COMMUNITY_AND_EVENTS', label: 'Community & Events' },
  { value: 'FURNITURE_INTERIORS', label: 'Furniture / Interiors' },
  { value: 'ELECTRONICS_AND_APPLIANCES', label: 'Electronics & Appliances' },
  { value: 'PERSONAL_AND_COMMUNITY', label: 'Personal & Community' },
  { value: 'JOBS_AND_SERVICES', label: 'Jobs & Services' },
  { value: 'FASHION_AND_LIFESTYLE', label: 'Fashion & Lifestyle' },
  { value: 'SAFETY_PROTECTION', label: 'Safety / Protection' },
  { value: 'BUSINESS_FINANCIAL', label: 'Business / Financial' },
  { value: 'PROFESSIONAL_SERVICES', label: 'Professional Services / Trades' },
  { value: 'INSURANCE_LEGAL', label: 'Insurance / Legal' },
  { value: 'MAINTENANCE_AND_REPAIR', label: 'Maintenance & Repair' },
  { value: 'PERSONAL_SERVICES', label: 'Personal Services' },
  { value: 'SALES_AUCTIONS', label: 'Sales / Auctions / Discounts' },
  { value: 'GIFTS_SPECIAL_OCCASIONS', label: 'Gifts / Special Occasions' },
  { value: 'TRAVEL_ENTERTAINMENT', label: 'Travel / Entertainment' },
  { value: 'WATER_FILTRATION', label: 'Water Filtration / Purification' },
  { value: 'SECURITY_GUARDS', label: 'Security Guards' },
  { value: 'OTHER_SERVICES', label: 'Other Services' },
] as const;

// Backend enum values (must match Listing.Condition exactly)
const CONDITIONS = [
  { value: 'NEW', label: 'Brand New', desc: 'Unused, sealed packaging' },
  { value: 'LIKE_NEW', label: 'Like New', desc: 'Barely used, no signs of wear' },
  { value: 'USED', label: 'Used', desc: 'Some wear, fully functional' },
  { value: 'REFURBISHED', label: 'Refurbished', desc: 'Restored to working condition' },
  { value: 'OTHER', label: 'Others', desc: 'Other condition' },
] as const;

// CITIES array commented out in favor of locationData
/*
const CITIES = [
  'Mumbai',
  'Delhi',
  'Bengaluru',
  'Hyderabad',
  'Chennai',
  'Pune',
  'Kolkata',
  'Ahmedabad',
  'Jaipur',
  'Lucknow',
];
*/

const STEPS = [
  { id: 1, title: 'Category', icon: Tag },
  { id: 2, title: 'Details', icon: FileText },
  { id: 3, title: 'Photos', icon: Camera },
  { id: 4, title: 'Review', icon: CheckCircle2 },
];

const PostAd: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Seller profile state
  const [sellerId, setSellerId] = useState<string | null>(null);
  const [sellerLoading, setSellerLoading] = useState(false);
  const [sellerError, setSellerError] = useState<string | null>(null);

  // Form data
  const [category, setCategory] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [price, setPrice] = useState('');
  const [condition, setCondition] = useState('');
  const [location, setLocation] = useState('');
  const [areaName, setAreaName] = useState('');
  const [selectedState, setSelectedState] = useState('');
  const [selectedCity, setSelectedCity] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');
  const [images, setImages] = useState<File[]>([]);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);
  const [uploadProgress, setUploadProgress] = useState<number[]>([]);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [coverIndex, setCoverIndex] = useState(0);

  // Synchronize location string with Area Name, State, and City fields
  useEffect(() => {
    if (areaName.trim() && selectedCity && selectedState) {
      const stateObj = locationData.find(item => item.code === selectedState);
      const stateName = stateObj ? stateObj.state : selectedState;
      setLocation(`${areaName.trim()}, ${selectedCity}, ${stateName}`);
    } else {
      setLocation('');
    }
  }, [areaName, selectedState, selectedCity]);

  // Redirect if not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { replace: true });
    }
  }, [isAuthenticated, navigate]);

   // Fetch seller profile using the logged-in user's ID
   useEffect(() => {
     if (!isAuthenticated || !user?.sub) return;

     const fetchOrCreateSeller = async () => {
       setSellerLoading(true);
       setSellerError(null);
       try {
         // Try to get existing seller profile
         const resp = await apiClient.get(`/api/sellers/user/${user.sub}`);
         if (resp?.id) {
           setSellerId(resp.id);
         } else {
           throw new Error('No seller ID in response');
         }
       } catch (err: any) {
         if (err?.status === 404 || err?.message?.includes('404')) {
           // Auto-create a seller profile on first use
           try {
             let realPhone = user.phone;
             try {
               const profile = await apiClient.get('/api/users/me');
               if (profile?.phone) {
                 realPhone = profile.phone;
               }
             } catch (pErr) {
               console.warn('Could not fetch user profile for phone fallback:', pErr);
             }

             const created = await apiClient.post('/api/sellers', {
               userId: user.sub,
               phone: realPhone || '0000000000',
               businessType: 'INDIVIDUAL',
               bio: `Seller profile for ${user.name || user.preferred_username || user.email}`,
             });
             if (created?.id) {
               setSellerId(created.id);
               // Note: Backend should update user's SELLER role when profile is created.
               // Token refresh will happen on next API request or manually if available.
             } else {
               setSellerError('Could not create seller profile. Please try again.');
             }
           } catch (createErr: any) {
             console.error('Seller creation error:', createErr);
             setSellerError(
               createErr?.message || 'Failed to set up seller profile. Please contact support.'
             );
           }
         } else {
           console.error('Seller fetch error:', err);
           setSellerError(err?.message || 'Failed to load seller profile.');
         }
       } finally {
         setSellerLoading(false);
       }
     };

     fetchOrCreateSeller();
   }, [isAuthenticated, user]);

  const handleImageAdd = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    // Reset input so the same file(s) can be re-selected
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    setImages((prev) => {
      if (prev.length + files.length > 4) {
        setError('Maximum 4 images allowed');
        return prev;
      }
      setError(null);

      // Generate previews for each file
      files.forEach((file) => {
        const reader = new FileReader();
        reader.onload = (ev) => {
          setImagePreviews((prevPreviews) => [...prevPreviews, ev.target?.result as string]);
        };
        reader.onerror = () => {
          // Still add a fallback preview so the count stays in sync
          setImagePreviews((prevPreviews) => [...prevPreviews, '']);
        };
        reader.readAsDataURL(file);
      });

      return [...prev, ...files];
    });
  };

  const removeImage = (index: number) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
    setImagePreviews((prev) => prev.filter((_, i) => i !== index));
    // Adjust coverIndex when an image is removed
    if (index === coverIndex) {
      setCoverIndex(0);
    } else if (index < coverIndex) {
      setCoverIndex((prev) => prev - 1);
    }
  };

  const addTag = () => {
    const tag = tagInput.trim();
    if (tag && !tags.includes(tag) && tags.length < 10) {
      setTags([...tags, tag]);
      setTagInput('');
    }
  };

  const removeTag = (index: number) => {
    setTags((prev) => prev.filter((_, i) => i !== index));
  };

  const handleTagKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addTag();
    }
  };

  const canProceed = (): boolean => {
    switch (step) {
      case 1:
        return !!category;
      case 2:
        return !!title.trim() && !!description.trim() && !!price && !!condition && !!location;
      case 3:
        return true; // Images optional
      case 4:
        return true;
      default:
        return false;
    }
  };

  const handleSubmit = async () => {
     if (!sellerId) {
       setError('Seller profile not ready. Please wait or refresh the page.');
       return;
     }

     setSubmitting(true);
     setError(null);
     try {
       const listing = await listingsAPI.create({
         sellerId,
         title: title.trim(),
         description: description.trim(),
         price: Math.round(parseFloat(price)), // backend expects Integer
         category,
         location,
         condition,
         tags,
       });

       if (images.length > 0 && listing?.id) {
         setUploadingImages(true);
         setUploadProgress(images.map(() => 0));
         let failedUploads = 0;

         for (let i = 0; i < images.length; i++) {
           const file = images[i];
           try {
             // Compress image client-side before upload
             const compressedFile = await imageCompression(file, {
               maxSizeMB: 1,
               maxWidthOrHeight: 1920,
               useWebWorker: true,
               onProgress: (percent) => {
                 setUploadProgress(prev => {
                   const updated = [...prev];
                   updated[i] = Math.round(percent);
                   return updated;
                 });
               },
             });

             // Upload as multipart/form-data
             const formData = new FormData();
             formData.append('file', compressedFile);
             formData.append('entityId', listing.id);
             formData.append('entityType', 'LISTING');
             formData.append('isPrimary', String(i === coverIndex));
             formData.append('displayOrder', String(i));

             const uploadResp = await apiClient.postForm('/api/images/multipart', formData);

             if (uploadResp?.cdnUrl || uploadResp?.originalUrl) {
               await apiClient.post(`/api/listings/${listing.id}/images`, {
                 imageUrl: uploadResp.cdnUrl || uploadResp.originalUrl,
                 thumbnailUrl: uploadResp.cdnThumbnailUrl || uploadResp.thumbnailUrl || uploadResp.cdnUrl || uploadResp.originalUrl,
                 isPrimary: String(i === coverIndex),
                 displayOrder: String(i),
               });
             }
           } catch (uploadErr: any) {
             failedUploads++;
             console.error(`Failed to upload image ${file.name}:`, uploadErr);
           }
         }
         setUploadingImages(false);
         if (failedUploads > 0) {
           setError(`Listing created but ${failedUploads} image(s) failed to upload. You can edit the listing to re-upload.`);
         }
       }

         setSuccess(true);
     } catch (err: any) {
       console.error('Submission error:', err);
       let msg = 'Failed to create listing';

       if (err?.status === 403) {
         msg = 'You do not have permission to create listings. Please ensure your seller profile is activated.';
       } else if (err?.status === 401) {
         msg = 'Your session has expired. Please log in again.';
         setTimeout(() => navigate('/login'), 2000);
       } else if (err?.message) {
         msg = err.message;
       } else if (err instanceof Error) {
         msg = err.message;
       }

       setError(msg);
     } finally {
       setSubmitting(false);
     }
   };

  if (success) {
    return (
      <div className="mx-auto flex min-h-[60vh] max-w-lg flex-col items-center justify-center px-4 py-20 text-center">
        <div className="mb-6 grid h-20 w-20 place-items-center rounded-full bg-green-500/10">
          <CheckCircle2 className="h-10 w-10 text-green-500" />
        </div>
        <h1 className="font-heading text-3xl font-bold text-foreground">Ad Posted Successfully!</h1>
        <p className="mt-3 text-muted-foreground">
          Your ad has been successfully submitted and is currently <span className="font-semibold text-amber-600">pending admin review</span>. We verify all listings to ensure they are genuine, trustworthy, and meet our quality standards. Approval is usually completed within 24 hours.
        </p>
        <p className="mt-2 text-sm text-muted-foreground">
          You can track its status in <span className="font-semibold">My Listings → Under Review</span>.
        </p>
        <p className="mt-2 text-sm text-amber-600/80">
          ⏳ Your ad will be expired and automatically removed after <span className="font-semibold">60 days</span>. All associated images will also be deleted.
        </p>
        <button
          onClick={() => navigate('/my-listings')}
          className="mt-6 inline-flex h-11 items-center gap-2 rounded-xl bg-primary px-6 text-sm font-semibold text-primary-foreground shadow-elevated transition hover:brightness-105"
        >
          Go to My Listings
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl px-4 py-8 md:py-12">
      <SEO
        title="Post Your Ad"
        description="Post a free classified ad on MemAds. Sell cars, mobile phones, bikes, furniture, electronics, and other items to buyers across India."
        canonical="/post"
      />
      {/* Page Header */}
      <div className="mb-8 text-center">
        <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-primary-soft px-4 py-1.5 text-xs font-semibold text-primary">
          <Sparkles className="h-3.5 w-3.5" /> Post Your Ad
        </div>
        <h1 className="font-heading text-3xl font-bold text-foreground md:text-4xl">
          Sell Something Today
        </h1>
        <p className="mt-2 text-muted-foreground">
          Reach millions of buyers across India in just a few steps.
        </p>
      </div>

       {/* Seller profile loading / error */}
       {sellerLoading && (
         <div className="mb-6 flex items-center gap-3 rounded-xl border border-border bg-secondary px-4 py-3 text-sm text-muted-foreground">
           <Loader2 className="h-4 w-4 animate-spin shrink-0" />
           Setting up your seller profile…
         </div>
       )}
       {sellerError && (
         <div className="mb-6 flex items-center gap-3 rounded-xl border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
           <AlertCircle className="h-5 w-5 shrink-0" />
           <div>
             <div className="font-semibold mb-1">Seller Profile Error</div>
             <div className="text-xs">{sellerError}</div>
             <button
               type="button"
               onClick={() => window.location.reload()}
               className="mt-2 text-xs font-semibold underline hover:no-underline"
             >
               Try again
             </button>
           </div>
         </div>
       )}

      {/* Step Indicator */}
      <div className="mb-10 flex items-center justify-center gap-0">
        {STEPS.map((s, i) => {
          const Icon = s.icon;
          const isActive = step === s.id;
          const isCompleted = step > s.id;
          return (
            <React.Fragment key={s.id}>
              {i > 0 && (
                <div
                  className={`hidden h-0.5 w-12 sm:block md:w-20 transition-colors duration-300 ${
                    isCompleted ? 'bg-primary' : 'bg-border'
                  }`}
                />
              )}
              <button
                type="button"
                onClick={() => {
                  if (isCompleted) setStep(s.id);
                }}
                disabled={!isCompleted && !isActive}
                className={`flex flex-col items-center gap-1.5 transition-all duration-300 ${
                  isActive
                    ? 'scale-105'
                    : isCompleted
                    ? 'cursor-pointer opacity-80 hover:opacity-100'
                    : 'cursor-default opacity-40'
                }`}
              >
                <span
                  className={`grid h-11 w-11 place-items-center rounded-xl border-2 transition-all duration-300 ${
                    isActive
                      ? 'border-primary bg-primary text-primary-foreground shadow-elevated'
                      : isCompleted
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-border bg-secondary text-muted-foreground'
                  }`}
                >
                  {isCompleted ? <CheckCircle2 className="h-5 w-5" /> : <Icon className="h-5 w-5" />}
                </span>
                <span
                  className={`text-xs font-medium ${
                    isActive ? 'text-primary' : isCompleted ? 'text-foreground' : 'text-muted-foreground'
                  }`}
                >
                  {s.title}
                </span>
              </button>
            </React.Fragment>
          );
        })}
      </div>

       {/* Error */}
       {error && (
         <div className="mb-6 flex items-center gap-3 rounded-xl border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
           <AlertCircle className="h-5 w-5 shrink-0" />
           <div className="flex-1">
             <div className="font-semibold mb-1">Error</div>
             <div className="text-xs">{error}</div>
           </div>
         </div>
       )}

      {/* Form Card */}
      <div className="rounded-2xl border border-border bg-card p-6 shadow-card md:p-8">
        {/* Step 1: Category */}
        {step === 1 && (
          <div>
            <h2 className="font-heading text-xl font-bold text-foreground mb-1">
              Choose a Category
            </h2>
            <p className="text-sm text-muted-foreground mb-6">
              Select the category that best fits your item.
            </p>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
              {CATEGORIES.map((cat) => (
                <button
                  key={cat.value}
                  type="button"
                  onClick={() => setCategory(cat.value)}
                  className={`rounded-xl border-2 px-4 py-3.5 text-left text-sm font-medium transition-all duration-200 ${
                    category === cat.value
                      ? 'border-primary bg-primary-soft text-primary shadow-sm'
                      : 'border-border bg-background text-foreground hover:border-primary/40 hover:bg-secondary'
                  }`}
                >
                  {cat.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 2: Details */}
        {step === 2 && (
          <div className="space-y-5">
            <h2 className="font-heading text-xl font-bold text-foreground mb-1">
              Ad Details
            </h2>
            <p className="text-sm text-muted-foreground mb-2">
              Provide information to help buyers find your listing.
            </p>

            {/* Title */}
            <div>
              <label htmlFor="post-title" className="mb-1.5 block text-sm font-semibold text-foreground">
                Title <span className="text-destructive">*</span>
              </label>
              <input
                id="post-title"
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. iPhone 15 Pro Max 256GB — Like New"
                maxLength={120}
                className="h-12 w-full rounded-xl border border-border bg-secondary px-4 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:bg-background focus:outline-none focus:ring-2 focus:ring-primary/15"
              />
              <span className="mt-1 block text-xs text-muted-foreground text-right">
                {title.length}/120
              </span>
            </div>

            {/* Description */}
            <div>
              <label htmlFor="post-desc" className="mb-1.5 block text-sm font-semibold text-foreground">
                Description <span className="text-destructive">*</span>
              </label>
              <textarea
                id="post-desc"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe your item in detail — condition, features, reason for selling..."
                rows={5}
                maxLength={2000}
                className="w-full rounded-xl border border-border bg-secondary px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:bg-background focus:outline-none focus:ring-2 focus:ring-primary/15 resize-none"
              />
              <span className="mt-1 block text-xs text-muted-foreground text-right">
                {description.length}/2000
              </span>
            </div>

            {/* Price + Condition Row */}
            <div className="grid gap-5 sm:grid-cols-2">
              <div>
                <label htmlFor="post-price" className="mb-1.5 block text-sm font-semibold text-foreground">
                  Price (₹) <span className="text-destructive">*</span>
                </label>
                <div className="relative">
                  <IndianRupee className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <input
                    id="post-price"
                    type="number"
                    min="1"
                    value={price}
                    onChange={(e) => setPrice(e.target.value)}
                    placeholder="0"
                    className="h-12 w-full rounded-xl border border-border bg-secondary pl-10 pr-4 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:bg-background focus:outline-none focus:ring-2 focus:ring-primary/15"
                  />
                </div>
              </div>
              <div>
                <label htmlFor="post-condition" className="mb-1.5 block text-sm font-semibold text-foreground">
                  Condition <span className="text-destructive">*</span>
                </label>
                <select
                  id="post-condition"
                  value={condition}
                  onChange={(e) => setCondition(e.target.value)}
                  className="h-12 w-full cursor-pointer appearance-none rounded-xl border border-border bg-secondary px-4 text-sm text-foreground focus:border-primary focus:bg-background focus:outline-none focus:ring-2 focus:ring-primary/15"
                >
                  <option value="">Select condition</option>
                  {CONDITIONS.map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label} — {c.desc}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Area Name */}
            <div>
              <label htmlFor="post-areaName" className="mb-1.5 block text-sm font-semibold text-foreground">
                <MapPin className="inline h-4 w-4 mr-1 -mt-0.5" />
                Area Name <span className="text-destructive">*</span>
              </label>
              <input
                id="post-areaName"
                type="text"
                value={areaName}
                onChange={(e) => setAreaName(e.target.value)}
                placeholder="Enter Area Name"
                className="h-12 w-full rounded-xl border border-border bg-secondary px-4 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:bg-background focus:outline-none focus:ring-2 focus:ring-primary/15"
              />
            </div>

            {/* State & City Grid */}
            <div className="grid gap-5 sm:grid-cols-2">
              {/* State */}
              <div>
                <label htmlFor="post-state" className="mb-1.5 block text-sm font-semibold text-foreground">
                  State <span className="text-destructive">*</span>
                </label>
                <select
                  id="post-state"
                  value={selectedState}
                  onChange={(e) => {
                    setSelectedState(e.target.value);
                    setSelectedCity('');
                  }}
                  className="h-12 w-full cursor-pointer appearance-none rounded-xl border border-border bg-secondary px-4 text-sm text-foreground focus:border-primary focus:bg-background focus:outline-none focus:ring-2 focus:ring-primary/15"
                >
                  <option value="">Select State</option>
                  {locationData.map((item) => (
                    <option key={item.code} value={item.code}>
                      {item.state}
                    </option>
                  ))}
                </select>
              </div>

              {/* City */}
              <div>
                <label htmlFor="post-city" className="mb-1.5 block text-sm font-semibold text-foreground">
                  City <span className="text-destructive">*</span>
                </label>
                <select
                  id="post-city"
                  value={selectedCity}
                  disabled={!selectedState}
                  onChange={(e) => setSelectedCity(e.target.value)}
                  className="h-12 w-full cursor-pointer appearance-none rounded-xl border border-border bg-secondary px-4 text-sm text-foreground focus:border-primary focus:bg-background focus:outline-none focus:ring-2 focus:ring-primary/15 disabled:opacity-50"
                >
                  <option value="">Select City</option>
                  {selectedState &&
                    locationData
                      .find((item) => item.code === selectedState)
                      ?.cities.map((city) => (
                        <option key={city} value={city}>
                          {city}
                        </option>
                      ))}
                </select>
              </div>
            </div>

            {/* Tags */}
            <div>
              <label htmlFor="post-tags" className="mb-1.5 block text-sm font-semibold text-foreground">
                Tags <span className="text-xs font-normal text-muted-foreground">(optional, up to 10)</span>
              </label>
              <div className="flex gap-2">
                <input
                  id="post-tags"
                  type="text"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={handleTagKeyDown}
                  placeholder="Type a tag and press Enter"
                  className="h-12 flex-1 rounded-xl border border-border bg-secondary px-4 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:bg-background focus:outline-none focus:ring-2 focus:ring-primary/15"
                />
                <button
                  type="button"
                  onClick={addTag}
                  disabled={!tagInput.trim()}
                  className="h-12 rounded-xl bg-secondary px-4 text-sm font-medium text-foreground transition hover:bg-primary-soft hover:text-primary disabled:opacity-40"
                >
                  Add
                </button>
              </div>
              {tags.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {tags.map((tag, i) => (
                    <span
                      key={i}
                      className="inline-flex items-center gap-1.5 rounded-lg bg-primary-soft px-3 py-1.5 text-xs font-medium text-primary"
                    >
                      {tag}
                      <button
                        type="button"
                        onClick={() => removeTag(i)}
                        className="hover:text-destructive transition"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Step 3: Photos */}
        {step === 3 && (
          <div>
            <h2 className="font-heading text-xl font-bold text-foreground mb-1">
              Add Photos
            </h2>
            <p className="text-sm text-muted-foreground mb-6">
              Upload up to 4 photos to showcase your item. Click the star to choose a cover photo.
            </p>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={handleImageAdd}
            />

            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
              {imagePreviews.map((src, i) => (
                <div
                  key={i}
                  className="group relative aspect-square overflow-hidden rounded-xl border border-border bg-secondary"
                >
                  <img src={src} alt={`Preview ${i + 1}`} className="h-full w-full object-cover" />

                  {/* Upload progress overlay */}
                  {uploadingImages && typeof uploadProgress[i] === 'number' && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center bg-background/80 backdrop-blur-sm">
                      <div className="h-1.5 w-3/4 overflow-hidden rounded-full bg-border">
                        <div
                          className="h-full rounded-full bg-primary transition-all duration-200"
                          style={{ width: `${uploadProgress[i] || 0}%` }}
                        />
                      </div>
                      <span className="mt-1.5 text-xs font-medium text-foreground">
                        {uploadProgress[i] || 0}%
                      </span>
                    </div>
                  )}

                  {!uploadingImages && (
                    <>
                      {i === coverIndex && (
                        <span className="absolute left-2 top-2 rounded-md bg-primary px-2 py-0.5 text-[10px] font-bold text-primary-foreground uppercase">
                          Cover
                        </span>
                      )}
                      <button
                        type="button"
                        onClick={() => setCoverIndex(i)}
                        title={i === coverIndex ? 'This is the cover photo' : 'Set as cover photo'}
                        className={`absolute left-2 ${i === coverIndex ? 'top-9' : 'top-2'} grid h-7 w-7 place-items-center rounded-full backdrop-blur transition ${
                          i === coverIndex
                            ? 'bg-amber-500 text-white opacity-100'
                            : 'bg-background/80 text-muted-foreground opacity-0 group-hover:opacity-100 hover:bg-amber-500 hover:text-white'
                        }`}
                      >
                        <Star className={`h-4 w-4 ${i === coverIndex ? 'fill-current' : ''}`} />
                      </button>
                      <button
                        type="button"
                        onClick={() => removeImage(i)}
                        className="absolute right-2 top-2 grid h-7 w-7 place-items-center rounded-full bg-background/80 text-destructive opacity-0 backdrop-blur transition group-hover:opacity-100 hover:bg-destructive hover:text-destructive-foreground"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </>
                  )}
                </div>
              ))}

              {images.length < 4 && (
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="flex aspect-square flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-border bg-secondary/50 text-muted-foreground transition hover:border-primary hover:bg-primary-soft hover:text-primary"
                >
                  <ImagePlus className="h-8 w-8" />
                  <span className="text-xs font-medium">Add Photo</span>
                </button>
              )}
            </div>

            {images.length === 0 && (
              <p className="mt-4 text-center text-xs text-muted-foreground">
                Photos are optional but ads with photos get 5x more responses.
              </p>
            )}
          </div>
        )}

        {/* Step 4: Review */}
        {step === 4 && (
          <div>
            <h2 className="font-heading text-xl font-bold text-foreground mb-1">
              Review Your Ad
            </h2>
            <p className="text-sm text-muted-foreground mb-6">
              Make sure everything looks good before posting.
            </p>

            <div className="space-y-4 rounded-xl border border-border bg-secondary/30 p-5">
              {/* Preview images */}
              {imagePreviews.length > 0 && (
                <div className="flex gap-2 overflow-x-auto pb-2">
                  {imagePreviews.map((src, i) => (
                    <div key={i} className="relative shrink-0">
                      <img
                        src={src}
                        alt={`Preview ${i + 1}`}
                        className={`h-20 w-20 rounded-lg object-cover border-2 ${i === coverIndex ? 'border-primary' : 'border-border'}`}
                      />
                      {i === coverIndex && (
                        <span className="absolute left-1 top-1 rounded bg-primary px-1.5 py-0.5 text-[8px] font-bold text-primary-foreground uppercase">
                          Cover
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}

              <div className="grid gap-3 text-sm">
                <div className="flex justify-between border-b border-border pb-2">
                  <span className="text-muted-foreground">Category</span>
                  <span className="font-medium text-foreground">
                    {CATEGORIES.find((c) => c.value === category)?.label || category}
                  </span>
                </div>
                <div className="flex justify-between border-b border-border pb-2">
                  <span className="text-muted-foreground">Title</span>
                  <span className="font-medium text-foreground text-right max-w-[60%]">{title}</span>
                </div>
                <div className="border-b border-border pb-2">
                  <span className="text-muted-foreground block mb-1">Description</span>
                  <span className="text-foreground text-xs leading-relaxed">{description}</span>
                </div>
                <div className="flex justify-between border-b border-border pb-2">
                  <span className="text-muted-foreground">Price</span>
                  <span className="font-bold text-primary text-lg">₹{Number(price).toLocaleString('en-IN')}</span>
                </div>
                <div className="flex justify-between border-b border-border pb-2">
                  <span className="text-muted-foreground">Condition</span>
                  <span className="font-medium text-foreground">
                    {CONDITIONS.find((c) => c.value === condition)?.label || condition}
                  </span>
                </div>
                <div className="flex justify-between border-b border-border pb-2">
                  <span className="text-muted-foreground">Location</span>
                  <span className="font-medium text-foreground">{location}</span>
                </div>
                {tags.length > 0 && (
                  <div className="pt-1">
                    <span className="text-muted-foreground block mb-2">Tags</span>
                    <div className="flex flex-wrap gap-1.5">
                      {tags.map((tag, i) => (
                        <span
                          key={i}
                          className="rounded-md bg-primary-soft px-2.5 py-1 text-xs font-medium text-primary"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Navigation Buttons */}
      <div className="mt-6 flex items-center justify-between">
        <button
          type="button"
          onClick={() => setStep((s) => Math.max(1, s - 1))}
          disabled={step === 1}
          className="inline-flex h-11 items-center gap-2 rounded-xl border border-border px-5 text-sm font-medium text-foreground transition hover:bg-secondary disabled:opacity-30 disabled:pointer-events-none"
        >
          <ChevronLeft className="h-4 w-4" />
          Back
        </button>

        {step < 4 ? (
          <button
            type="button"
            onClick={() => setStep((s) => Math.min(4, s + 1))}
            disabled={!canProceed()}
            className="inline-flex h-11 items-center gap-2 rounded-xl bg-primary px-6 text-sm font-semibold text-primary-foreground shadow-elevated transition hover:brightness-105 disabled:opacity-40 disabled:pointer-events-none"
          >
            Next
            <ChevronRight className="h-4 w-4" />
          </button>
        ) : (
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting || uploadingImages || sellerLoading || !!sellerError || !sellerId}
            className="inline-flex h-12 items-center gap-2 rounded-xl bg-gradient-accent px-8 text-sm font-bold text-accent-foreground shadow-elevated transition hover:brightness-105 disabled:opacity-60"
          >
            {submitting || uploadingImages ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                {uploadingImages ? 'Uploading images...' : 'Posting...'}
              </>
            ) : (
              <>
                <Sparkles className="h-4 w-4" />
                Post My Ad
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
};

export default PostAd;
